import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const isRegister = mode === "register";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && !legalAccepted) {
      toast.error("Please agree to the Terms, Privacy Policy, and Risk Disclosure to continue.");
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name, { legal_accepted: true });
        toast.success("Welcome to UpTick!");
      } else {
        await login(email, password);
        toast.success("Welcome back");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6 py-12 grain">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 group" data-testid="auth-logo">
          <div className="w-9 h-9 rounded-lg bg-[#2A2F2B] flex items-center justify-center transition-soft group-hover:rotate-12">
            <Sparkles className="w-4 h-4 text-[#D18E63]" strokeWidth={2} />
          </div>
          <span className="font-heading text-2xl font-bold text-[#2A2F2B]">uptick</span>
        </Link>

        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 animate-fade-up">
          <h1 className="font-heading text-3xl tracking-tight font-medium text-[#2A2F2B] mb-1">
            {isRegister ? "Create account" : "Welcome back"}
          </h1>
          <p className="text-sm text-[#6B7068] mb-6">
            {isRegister
              ? "Start your beginner-friendly trading journey."
              : "Sign in to track your watchlist."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                  Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                  className="mt-1.5 rounded-lg border-[#EAE6E1] focus:border-[#547A8F]"
                  placeholder="Alex Trader"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                className="mt-1.5 rounded-lg border-[#EAE6E1] focus:border-[#547A8F]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                className="mt-1.5 rounded-lg border-[#EAE6E1] focus:border-[#547A8F]"
                placeholder="••••••••"
              />
            </div>

            {isRegister && (
              <label
                className="flex items-start gap-3 text-xs text-[#6B7068] dark:text-[#8B949E] cursor-pointer select-none"
                data-testid="auth-legal-consent"
              >
                <input
                  type="checkbox"
                  required
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                  data-testid="auth-legal-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-[#EAE6E1] dark:border-[#2A3340] accent-[#2EA44F] flex-shrink-0"
                  aria-label="Agree to legal documents"
                />
                <span className="leading-relaxed">
                  I'm 18+ and I agree to UpTick's{" "}
                  <Link to="/legal/terms" target="_blank" rel="noopener" className="text-[#547A8F] dark:text-[#3FB962] underline" data-testid="auth-legal-terms-link">
                    Terms
                  </Link>
                  ,{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener" className="text-[#547A8F] dark:text-[#3FB962] underline" data-testid="auth-legal-privacy-link">
                    Privacy Policy
                  </Link>
                  , and{" "}
                  <Link to="/legal/risk" target="_blank" rel="noopener" className="text-[#547A8F] dark:text-[#3FB962] underline" data-testid="auth-legal-risk-link">
                    Risk Disclosure
                  </Link>
                  . I understand UpTick is an educational tool — not financial advice.
                </span>
              </label>
            )}

            <Button
              type="submit"
              disabled={loading || (isRegister && !legalAccepted)}
              data-testid="auth-submit-btn"
              className="w-full rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white py-6 transition-soft"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isRegister ? (
                "Create account"
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <p className="text-sm text-center text-[#6B7068] mt-6">
            {isRegister ? "Already have an account?" : "New to UpTick?"}{" "}
            <Link
              to={isRegister ? "/login" : "/register"}
              className="text-[#547A8F] hover:text-[#426071] font-medium"
              data-testid="auth-switch-link"
            >
              {isRegister ? "Sign in" : "Create one"}
            </Link>
          </p>
          {!isRegister && (
            <p className="text-xs text-center text-[#6B7068] mt-2">
              <Link to="/forgot-password" className="hover:text-[#547A8F]" data-testid="forgot-link">
                Forgot password?
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
